import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import mapContext from '../contexts/map-context';
import { getActiveFilters } from '../utils/activeFilters';
import { getRadiusInKm } from '../utils/distance';
import { useGeolocation } from './useMapGeolocation';
import { connectDealerToDealerContext } from '../utils/connectDealer';
import { ActionTypes } from '../contexts/reducers/map-reducer';
import useLazySearchByGeoLocation from './useLazySearchByGeoLocation';

type ConnectByGeoOpts = {
  enabled: boolean;
};

const isCoordinates = (value: unknown): value is { lat: number; lng: number } => {
  if (!value || typeof value !== 'object') return false;
  const maybeCoordinates = value as { lat?: unknown; lng?: unknown };
  return typeof maybeCoordinates.lat === 'number' && typeof maybeCoordinates.lng === 'number';
};

export function useConnectDealerByGeo({ enabled }: ConnectByGeoOpts = { enabled: false }) {
  const dispatch = mapContext.useMapDispatch();

  const {
    userSelectedFilters,
    preselectedFilters,
    searchRadius,
    locale,
    intlSearchEnabled,
    distanceUnit,
    dealerContextService,
    connectedDealer,
  } = mapContext.useMapState();

  const activeFilters = useMemo(
    () => getActiveFilters(preselectedFilters, userSelectedFilters),
    [preselectedFilters, userSelectedFilters],
  );

  const [userCoordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const firstRun = useRef(true);

  const searchRadiusInKm = getRadiusInKm(searchRadius, distanceUnit);
  const { getUserPosition } = useGeolocation();

  const getUserLocation = useCallback(async () => {
    const { coordinates } = await getUserPosition();
    setCoordinates(isCoordinates(coordinates) ? coordinates : null);
  }, [getUserPosition]);

  const { loadDealers, loading, error, dealersResult } = useLazySearchByGeoLocation(
    searchRadiusInKm,
    userCoordinates?.lat ?? 0,
    userCoordinates?.lng ?? 0,
    intlSearchEnabled ? undefined : locale.countryCode,
    activeFilters,
    userCoordinates?.lat ?? 0,
    userCoordinates?.lng ?? 0,
  );

  useEffect(() => {
    if (!enabled) {
      firstRun.current = true;
      return;
    }

    void (async () => {
      try {
        await getUserLocation();
      } catch {
        /* swallow; error handled inside getUserPosition */
      }
    })();
  }, [enabled, getUserLocation]);

  useEffect(() => {
    if (!enabled) return;
    if (!userCoordinates) return;

    if (firstRun.current) {
      firstRun.current = false;
      loadDealers();
    }
  }, [enabled, userCoordinates, loadDealers]);

  const lastConnectedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (loading || error || !dealersResult?.length || !dealerContextService) return;

    const newDealerId = dealersResult[0].dealerId;
    if (connectedDealer === newDealerId || lastConnectedRef.current === newDealerId) return;

    connectDealerToDealerContext(newDealerId, dealerContextService, dispatch);
    dispatch({ type: ActionTypes.setConnectedDealer, payload: newDealerId });
    lastConnectedRef.current = newDealerId;
  }, [enabled, loading, error, dealersResult, dealerContextService, connectedDealer, dispatch]);
}
